import { useEffect, useState, useCallback } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { Plus, Trash2, BarChart3, Power } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPolls() {
  const { authFetch } = useAdmin();
  const [polls, setPolls] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [question, setQuestion] = useState('');
  const [opts, setOpts] = useState(['', '']);
  const [allowMultiple, setAllowMultiple] = useState(false);
  const [results, setResults] = useState({});

  const load = useCallback(async () => {
    const r = await authFetch(`${API}/admin/polls`);
    const d = await r.json();
    setPolls(d.polls || []);
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  async function loadResults(pid) {
    const r = await authFetch(`${API}/admin/polls/${pid}/results`);
    const d = await r.json();
    setResults({ ...results, [pid]: d });
  }

  async function create() {
    const options = opts.map(s => s.trim()).filter(Boolean).map(label => ({ label }));
    if (!question.trim() || options.length < 2) {
      toast.error('Pitanje i najmanje 2 opcije'); return;
    }
    const r = await authFetch(`${API}/admin/polls`, {
      method: 'POST',
      body: JSON.stringify({ question, options, allow_multiple: allowMultiple, is_active: true }),
    });
    if (r.ok) { toast.success('Anketa kreirana'); setShowNew(false); setQuestion(''); setOpts(['', '']); setAllowMultiple(false); load(); }
    else toast.error('Greška');
  }

  async function toggle(p) {
    const r = await authFetch(`${API}/admin/polls/${p.id}`, {
      method: 'PUT', body: JSON.stringify({ is_active: !p.is_active }),
    });
    if (r.ok) { toast.success(p.is_active ? 'Zatvoreno' : 'Aktivirano'); load(); }
  }

  async function remove(id) {
    if (!window.confirm('Obrisati anketu i sve glasove?')) return;
    const r = await authFetch(`${API}/admin/polls/${id}`, { method: 'DELETE' });
    if (r.ok) { toast.success('Obrisano'); load(); }
  }

  return (
    <div className="p-6 text-zinc-100 max-w-6xl" data-testid="admin-polls-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Ankete</h1>
        <button onClick={() => setShowNew(v => !v)} className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-sm font-medium inline-flex items-center gap-2" data-testid="polls-new-btn">
          <Plus size={16} /> Nova anketa
        </button>
      </div>

      {showNew && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 mb-6 space-y-3" data-testid="polls-new-form">
          <input value={question} onChange={e => setQuestion(e.target.value)} placeholder="Pitanje"
            className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm" data-testid="polls-new-question" />
          {opts.map((o, i) => (
            <div key={i} className="flex gap-2">
              <input value={o} onChange={e => { const n = [...opts]; n[i] = e.target.value; setOpts(n); }}
                placeholder={`Opcija ${i + 1}`} className="flex-1 bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm" data-testid={`polls-new-opt-${i}`} />
              {opts.length > 2 && <button onClick={() => setOpts(opts.filter((_, j) => j !== i))} className="px-3 bg-zinc-800 rounded text-zinc-400">×</button>}
            </div>
          ))}
          <div className="flex items-center gap-4 text-sm">
            <button onClick={() => setOpts([...opts, ''])} className="text-red-400 hover:text-red-300">+ Dodaj opciju</button>
            <label className="inline-flex items-center gap-2"><input type="checkbox" checked={allowMultiple} onChange={e => setAllowMultiple(e.target.checked)} /> Više odgovora dopušteno</label>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowNew(false)} className="px-4 py-2 text-sm text-zinc-400">Odustani</button>
            <button onClick={create} className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded text-sm" data-testid="polls-new-save">Spremi</button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {polls.length === 0 && <div className="text-zinc-500 py-10 text-center">Nema anketa.</div>}
        {polls.map(p => (
          <div key={p.id} className="bg-zinc-900 border border-zinc-800 rounded-lg p-4" data-testid={`poll-row-${p.id}`}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="font-medium">{p.question}</div>
                <div className="text-xs text-zinc-500 mt-1">
                  {p.options.length} opcija · {p.allow_multiple ? 'multi-select' : 'jedan odgovor'} ·
                  <span className={`ml-1 ${p.is_active ? 'text-green-400' : 'text-zinc-500'}`}>{p.is_active ? 'aktivna' : 'zatvorena'}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => loadResults(p.id)} className="p-2 hover:bg-zinc-800 rounded" title="Rezultati"><BarChart3 size={15} /></button>
                <button onClick={() => toggle(p)} className="p-2 hover:bg-zinc-800 rounded" title={p.is_active ? 'Zatvori' : 'Aktiviraj'}><Power size={15} /></button>
                <button onClick={() => remove(p.id)} className="p-2 hover:bg-zinc-800 rounded text-red-400" title="Obriši"><Trash2 size={15} /></button>
              </div>
            </div>
            <div className="text-[11px] text-zinc-600 font-mono">ID: {p.id}</div>
            {results[p.id] && (
              <div className="mt-3 pt-3 border-t border-zinc-800 text-sm">
                <div className="text-xs text-zinc-500 mb-2">Ukupno glasova: {results[p.id].total_votes}</div>
                {p.options.map(o => {
                  const c = results[p.id].counts[o.id] || 0;
                  const pct = results[p.id].total_votes ? Math.round((c / results[p.id].total_votes) * 100) : 0;
                  return (
                    <div key={o.id} className="mb-2">
                      <div className="flex justify-between text-xs mb-1"><span>{o.label}</span><span className="text-zinc-400">{c} ({pct}%)</span></div>
                      <div className="h-2 bg-zinc-800 rounded overflow-hidden"><div className="h-full bg-red-600" style={{ width: `${pct}%` }} /></div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
